import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Shuffle, Download, Share2, Settings } from 'lucide-react';
import logo from './logo.gif';
import SEO from '@americanexpress/react-seo';

const TelegramBingoGenerator = () => {
  const [phrases, setPhrases] = useState([
    "Oink", "Any dom?", "Woff", "Into?", "Fuck yeah", "Slut", "Need to shower", "Im a whore", "LMFAO", "No music?", "Just slammed", "Thank's for hosting", "I'm so high", "daddy", "PM is not working", "Pupmask", "Who turned off my cam?", "Wanna see my hole?", "Any top?", "Dose?", "Spotlight is on", "Cloud?", "Any poz?", "I cant see any cams", "SPAM", "brb", "😈", "Why was I sent to WR?", "Anyone watching me?", "multipin?", "LOL", "Is he okay?", "My pins!", "Add my Telegram", "Slam?", "high", "Last page", "p*rv?", "phone towards cam", "HSW sleeping", "Swirly", "Need to eat", "CAPS LOCK ONLY", "Bye", "I was removed", "Anyone else lagging?"
  ]);
  
  const [newPhrase, setNewPhrase] = useState('');
  const [bingoCard, setBingoCard] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Initialize Telegram WebApp
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.ready();
      tg.expand();
      
      // Set theme colors
      document.documentElement.style.setProperty('--tg-theme-bg-color', tg.themeParams.bg_color || '#ffffff');
      document.documentElement.style.setProperty('--tg-theme-text-color', tg.themeParams.text_color || '#000000');
      document.documentElement.style.setProperty('--tg-theme-hint-color', tg.themeParams.hint_color || '#999999');
      document.documentElement.style.setProperty('--tg-theme-link-color', tg.themeParams.link_color || '#2481cc');
      document.documentElement.style.setProperty('--tg-theme-button-color', tg.themeParams.button_color || '#2481cc');
      document.documentElement.style.setProperty('--tg-theme-button-text-color', tg.themeParams.button_text_color || '#ffffff');
    }
  }, []);

  const addPhrase = () => {
    if (newPhrase.trim() && !phrases.includes(newPhrase.trim())) {
      setPhrases([...phrases, newPhrase.trim()]);
      setNewPhrase('');
    }
  };

  const removePhrase = (index) => {
    setPhrases(phrases.filter((_, i) => i !== index));
  };

  const generateBingoCard = () => {
    if (phrases.length < 24) {
      alert('Need at least 24 phrases to generate a bingo card!');
      return;
    }

    setIsGenerating(true);
    
    // Simulate generation delay for better UX
    setTimeout(() => {
      const shuffled = [...phrases].sort(() => Math.random() - 0.5);
      const card = shuffled.slice(0, 24);
      
      // Insert FREE space in the middle (index 12)
      card.splice(12, 0, 'FREE');
      
      setBingoCard(card);
      setIsGenerating(false);
    }, 800);
  };

  const shareBingoCard = async () => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      const cardText = bingoCard.map((phrase, index) => {
        if (index % 5 === 0) return `\n${phrase}`;
        return phrase;
      }).join(' | ');
      
      const message = `🎯 **PNP BINGO CARD** 🎯\n\`\`\`${cardText}\`\`\`\n\nGenerated with @YourBotName`;
      
      tg.switchInlineQuery(message);
    }
  };

  const downloadCard = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 500;
    canvas.height = 500;
    
    // Background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, 500, 500);
    
    // Grid
    ctx.strokeStyle = '#16213e';
    ctx.lineWidth = 2;
    
    for (let i = 0; i <= 5; i++) {
      ctx.beginPath();
      ctx.moveTo(i * 100, 0);
      ctx.lineTo(i * 100, 500);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(0, i * 100);
      ctx.lineTo(500, i * 100);
      ctx.stroke();
    }
    
    // Text
    ctx.fillStyle = '#ffffff';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    bingoCard.forEach((phrase, index) => {
      const row = Math.floor(index / 5);
      const col = index % 5;
      const x = col * 100 + 50;
      const y = row * 100 + 50;
      
      // Word wrap for long phrases
      const words = phrase.split(' ');
      const lines = [];
      let currentLine = '';
      
      words.forEach(word => {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const metrics = ctx.measureText(testLine);
        if (metrics.width > 80 && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });
      lines.push(currentLine);
      
      lines.forEach((line, lineIndex) => {
        const lineY = y + (lineIndex - (lines.length - 1) / 2) * 16;
        ctx.fillText(line, x, lineY);
      });
    });
    
    // Download
    const link = document.createElement('a');
    link.download = 'bingo-card.png';
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white">
      <div className="container mx-auto px-4 py-6 max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
            PNP Bingo Generator
          </h1>
          <p className="text-blue-200 text-sm">Play with your friends or a random tweaker!</p>
        </div>

        {/* Navigation */}
        <div className="flex justify-center mb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-1 flex">
            <button
              onClick={() => setShowSettings(false)}
              className={`px-4 py-2 rounded-lg transition-all ${
                !showSettings 
                  ? 'bg-blue-500 text-white shadow-lg' 
                  : 'text-blue-200 hover:text-white'
              }`}
            >
              Generate
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className={`px-4 py-2 rounded-lg transition-all ${
                showSettings 
                  ? 'bg-blue-500 text-white shadow-lg' 
                  : 'text-blue-200 hover:text-white'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-1" />
              Phrases
            </button>
          </div>
        </div>

        {showSettings ? (
          /* Phrase Management */
          <div className="space-y-6">
            {/* Add Phrase */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <h3 className="text-lg font-semibold mb-3 text-blue-200">Add New Phrase</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newPhrase}
                  onChange={(e) => setNewPhrase(e.target.value)}
                  placeholder="Enter a phrase..."
                  className="flex-1 px-3 py-2 bg-white/20 rounded-lg placeholder-blue-200 text-white border border-white/30 focus:border-blue-400 focus:outline-none"
                  onKeyPress={(e) => e.key === 'Enter' && addPhrase()}
                />
                <button
                  onClick={addPhrase}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Phrase List */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <h3 className="text-lg font-semibold mb-3 text-blue-200">
                Phrases ({phrases.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {phrases.map((phrase, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <span className="text-sm">{phrase}</span>
                    <button
                      onClick={() => removePhrase(index)}
                      className="p-1 text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Bingo Card Generation */
          <div className="space-y-6">
            {/* Generate Button */}
            <div className="text-center">
              <button
                onClick={generateBingoCard}
                disabled={isGenerating || phrases.length < 24}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl font-semibold text-white shadow-lg hover:from-green-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Shuffle className="w-4 h-4" />
                    Generate New Card
                  </div>
                )}
              </button>
              {phrases.length < 24 && (
                <p className="text-sm text-yellow-300 mt-2">
                  Need {24 - phrases.length} more phrases to generate a card
                </p>
              )}
            </div>

            {/* Bingo Card */}
            {bingoCard.length > 0 && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="grid grid-cols-5 gap-1 mb-4">
                  {bingoCard.map((phrase, index) => (
                    <div
                      key={index}
                      className={`
                        aspect-square flex items-center justify-center text-center p-1 rounded-lg text-xs font-medium
                        ${index === 12 
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-black' 
                          : 'bg-white/20 text-white hover:bg-white/30'
                        }
                        transition-all cursor-pointer
                      `}
                    >
                      {phrase}
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={shareBingoCard}
                    className="px-4 py-2 bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                  <button
                    onClick={downloadCard}
                    className="px-4 py-2 bg-purple-500 rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>     )}
          </div>
        )}
      </div>
      <div className="flex gap-2 justify-center logo">
      <img src={logo} alt="Logo" />
    <SEO
      title="PNP Bingo Generator"
      description="Play with your friends or a random tweaker!"
      keywords={['pnp', 'tweaker']}
      siteUrl="https://bingo.primeupyour.life"
      image={{
        src: 'https://bingo.primeupyour.life/seo.png'
      }}
    />
      </div>
    </div>
  );
};

export default TelegramBingoGenerator;